import React, { useEffect, useState } from 'react';
import { simboloMoneda } from '../services/negocioActual';
import { obtenerResumenDashboard } from '../services/dashboardService';
import { iconoTipo } from '../services/cuentasService';
import { mensajeAmigable } from '../utils/errores';
import MensajePrueba from './MensajePrueba';
import HoyGraficas from './HoyGraficas';
import { usaProductos } from '../utils/queVende';
import MetaInicioCard from './MetaInicioCard';

const fmt = (n) => {
  const v = Number(n || 0);
  return `${v < 0 ? '-' : ''}${simboloMoneda()} ${Math.abs(v).toLocaleString('es-HN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const VERDE = 'var(--k-verde, #0D9488)';
const ROJO = '#E02424';
const ACENTO = 'var(--k-accent, var(--color-primario, #6D28D9))';

// Tarjeta de indicador (mismo estilo que el panel: número en DM Mono, etiquetas
// suaves). `tone` colorea el número; `hero` la resalta con el color de marca.
const Tile = ({ lbl, num, sub, tone, hero, onClick }) => (
  <div className={`k-tile${hero ? ' hero' : ''}${onClick ? ' clic' : ''}`} onClick={onClick}>
    <span className="lbl">{lbl}</span>
    <span className="num" style={tone ? { color: tone } : undefined}>{num}</span>
    {sub && <span className="sub">{sub}</span>}
  </div>
);

// Lo que se muestra depende de los permisos (la dueña ve todo):
// verCostos (ganancias y costos), verCuentas (saldos), verGastos, soloDe (ventas de una persona).
export default function DashboardView({ negocio, verCostos = true, verCuentas = true, verGastos = true, soloDe = null, onIrStockBajo, onIrCuentas, onIrGastos, onIrVentasHoy, onIr, onIrMetas }) {
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let activo = true;
    obtenerResumenDashboard({ soloDe })
      .then((d) => activo && setDatos(d))
      .catch((e) => activo && setError(mensajeAmigable(e)));
    return () => { activo = false; };
  }, [soloDe]);

  const saludo = (() => {
    const h = new Date().getHours();
    if (h < 12) return 'Buenos días';
    if (h < 19) return 'Buenas tardes';
    return 'Buenas noches';
  })();

  return (
    <div className="k-page">
      <h2 className="k-page-title" style={{ margin: '0 0 4px' }}>
        {saludo}, {negocio?.nombre || 'tu negocio'} 👋
      </h2>
      <p style={{ margin: '0 0 20px', color: 'var(--k-muted)', fontSize: '14px' }}>
        Así va tu negocio hoy · {new Date().toLocaleDateString('es-HN', { weekday: 'long', day: 'numeric', month: 'long' })}
      </p>

      {onIr && <MensajePrueba onIr={onIr} />}

      {!datos && !error && (
        <div style={{ textAlign: 'center', padding: '48px', color: 'var(--k-muted)' }}>
          <div style={{ fontSize: '32px', marginBottom: '8px' }}>⏳</div>
          <p style={{ margin: 0, fontWeight: 600 }}>Calculando tu resumen...</p>
        </div>
      )}
      {error && (
        <div style={{ textAlign: 'center', padding: '32px', backgroundColor: '#FEE2E2', borderRadius: '14px', border: '1px solid #FCA5A5' }}>
          <p style={{ margin: 0, color: '#991B1B', fontWeight: 600 }}>⚠️ {error}</p>
        </div>
      )}

      {datos && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {/* Fila 1: hoy */}
          <div className="k-kpis">
            <Tile lbl={soloDe ? 'Tus ventas de hoy' : 'Ventas de hoy'} num={fmt(datos.totalHoy)} sub={`${datos.unidadesHoy} producto(s)${datos.ventasLibresHoy ? ` + ${datos.ventasLibresHoy} venta(s) libre(s)` : ''}${datos.enviosHoy ? ` · aparte ${fmt(datos.enviosHoyMonto)} de envíos (para el courier)` : ''} · toca para ver`} tone={ACENTO} onClick={onIrVentasHoy} />
            {verCostos && <Tile lbl="Ganancia estimada hoy" num={fmt(datos.gananciaHoy)} sub="Venta menos costo" tone={VERDE} />}
            {verGastos && <Tile lbl="Gastos de hoy" num={fmt(datos.gastosHoy)} tone={ROJO} />}
          </div>

          {/* 🎯 Mi meta del mes (solo la dueña) */}
          {onIrMetas && <MetaInicioCard onIrMetas={onIrMetas} />}

          {/* Gráficas: hoy por hora, contra el promedio y últimos 7 días */}
          <HoyGraficas datos={datos} verGastos={verGastos} verCostos={verCostos} />

          {/* Fila 2: dinero y 7 días */}
          {(verCuentas || verCostos) && <div className="k-kpis">
            {verCuentas && <Tile lbl="💰 Dinero disponible" num={fmt(datos.disponible)} sub="En todas tus cuentas · toca para ver" tone={ACENTO} hero onClick={onIrCuentas} />}
            {verCuentas && datos.deuda < 0 ? (
              <Tile lbl="🪙 Deuda en tarjetas" num={fmt(datos.deuda)} sub="Toca para ver" tone={ROJO} onClick={onIrCuentas} />
            ) : verCostos && verGastos && (
              <Tile lbl="Utilidad (últimos 7 días)" num={fmt(datos.utilidad7)} sub="Ventas − costo − gastos" tone={datos.utilidad7 >= 0 ? VERDE : ROJO} />
            )}
            {verCostos && <Tile lbl="Vendido (últimos 7 días)" num={fmt(datos.ventas7Total)} />}
          </div>}

          {/* Cuentas resumidas */}
          {verCuentas && datos.cuentas.length > 0 && (
            <div className="k-card" style={{ padding: '18px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <p className="k-sec-title">🏦 Tus cuentas</p>
                <button onClick={onIrCuentas} className="k-link">Ver todo →</button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '10px' }}>
                {datos.cuentas.map((c) => (
                  <div key={c.id} style={{ padding: '11px 12px', backgroundColor: '#F6F3FC', borderRadius: '11px', border: '1px solid var(--k-hairline)' }}>
                    <p style={{ margin: '0 0 3px', fontSize: '12px', color: 'var(--k-muted)' }}>{iconoTipo(c.tipo)} {c.nombre}</p>
                    <p style={{ margin: 0, fontSize: '16px', fontWeight: 700, fontFamily: "'DM Mono', monospace", color: Number(c.saldo) < 0 ? ROJO : VERDE }}>{fmt(c.saldo)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Alertas / cosas por atender */}
          <div className="k-card" style={{ padding: '18px' }}>
            <p className="k-sec-title" style={{ marginBottom: '12px' }}>📌 Por atender</p>
            {datos.stockBajo.length === 0 ? (
              <p style={{ margin: 0, fontSize: '13px', color: 'var(--k-faint)' }}>¡Todo en orden! No hay alertas por ahora. 🎉</p>
            ) : (
              <button onClick={onIrStockBajo} disabled={!onIrStockBajo}
                style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%', textAlign: 'left', backgroundColor: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: '10px', padding: '12px', cursor: onIrStockBajo ? 'pointer' : 'default' }}>
                <span style={{ fontSize: '20px' }}>⚠️</span>
                <span style={{ fontSize: '13px', color: '#92400E', fontWeight: 600 }}>
                  {datos.stockBajo.length} producto(s) con stock bajo o agotado{onIrStockBajo ? ' — toca para reponer' : ''}
                </span>
              </button>
            )}
            {usaProductos(negocio?.vende) && <p style={{ margin: '12px 0 0', fontSize: '12px', color: 'var(--k-faint)' }}>
              Inventario: {datos.totalProductos} productos{verCostos && <> · valor a costo {fmt(datos.valorInventarioCosto)}</>}
            </p>}
          </div>
        </div>
      )}
    </div>
  );
}
